import { ActiveUserStore } from '@active-user-management';
import { CdkCopyToClipboard } from '@angular/cdk/clipboard';
import { afterNextRender, Component, inject, output } from '@angular/core';
import { SidenavService } from '@app-layout/sidenav/data-access';
import { AuthStore } from '@auth/data-access';
import { LanguageService } from '@core/language';
import { UserStore } from '@data-users';
import { ActiveUserEditProfileComponent } from '@feature-user-management';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageType } from '@shared-types';
import { DialogDirective } from '@shared/dialog';
import { DcIconComponent, IconComponent } from '@shared/icon';
import { ToastService } from '@utils';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';

@Component({
	selector: 'dc-sidenav-user-profile',
	templateUrl: './sidenav-user-profile.component.html',
	styleUrls: ['./sidenav-user-profile.component.scss'],
	imports: [TranslateModule, DcIconComponent, OverlayPanelModule, IconComponent, ActiveUserEditProfileComponent, DialogDirective, CdkCopyToClipboard],
})
export class SidenavUserProfileComponent {
	readonly closeOverlay = output<void>();

	// Services
	protected readonly authStore = inject(AuthStore);
	protected readonly userStore = inject(UserStore);
	protected readonly languageService = inject(LanguageService);
	protected readonly sidenavService = inject(SidenavService);
	protected readonly toastService = inject(ToastService);
	readonly activeUserStore: ActiveUserStore = inject(ActiveUserStore);

	/**
	 * Changes the application language with improved UX
	 * @param lang Language code ('he', 'en', 'es')
	 */
	protected async changeLanguage(lang: LanguageType, userProfilePanel: OverlayPanel): Promise<void> {
		// Show loading indicator if needed (optional)
		const currentLang = this.languageService.currentLang$();
		
		if (currentLang === lang) {
			console.debug('Language already selected:', lang);
			return;
		}

		try {
			const success = await this.languageService.setAppLanguage(lang, true);
			
			if (success) {
				// Optional: Close overlay after successful change
				this.closeOverlay.emit();
				userProfilePanel.hide();
				// Show success message
				this.toastService.success('language.change.success');
			}
		} catch (error) {
			console.error('Failed to change language in UI component:', error);
			// Error is already handled in the service
		}
	}

	constructor() {
		afterNextRender(() => {
			if (this.activeUserStore.isAuthenticated()) {
				this.activeUserStore.Portrait.load().subscribe();
			}
		});
	}
	/**
	 * Handles logout action
	 */
	protected logout(): void {
		this.authStore.logout();
		this.closeOverlay.emit();
	}

	/**
	 * Handles edit profile action
	 * User will implement this event
	 */
	protected editProfile(activeUserEditProfileDialog: DialogDirective): void {
		// Emit event that can be caught by a parent component
		this.closeOverlay.emit();
		// Additional logic can be added here
		activeUserEditProfileDialog.open();
	}

	protected onProfileUpdated(activeUserEditProfileDialog: DialogDirective): void {
		activeUserEditProfileDialog.close();
		this.toastService.success('struct.user-edit.updatesuccess');
	}
}
